import api from '~/services/api';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
  useMemo,
} from 'react';

import { FaRegWindowRestore } from 'react-icons/fa';

import { Span } from '../Span';
import { AlertBox } from './styles';
import { ModalContabil } from './Components/Modal';
import { CustomButtonNovo } from '../Buttons/CustomButtonNovo';

interface FormProps {
  value: string | number | undefined;
  isRequired?: boolean;
  min?: number;
  max?: number;
  maxLength?: number;
  isUndefined?: boolean;
  isNull?: boolean;
  onChange(data: any, data1: boolean, codConta: number): any;
  setInvalid?: boolean;
  iniInicializado?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  label?: string;
  lengthBuscaConta?: number;
  focus?: boolean;
  placeholder?: string;
}
type Conta = {
  cod_conta: number;
  conta_contabil: string;
  conta_reduzida: string;
  des_conta_contabil: string;
};

/**
 * @function onChange Função que atualiza o valor do input
 * @returns Componente para validação de inputs
 * @value Valor correspondente ao cod_conta
 */
const ContaContabil: React.FC<FormProps> = (props) => {
  const {
    placeholder = 'Número conta reduzida',
    value,
    isRequired = false,
    onChange,
    min = 100,
    max = 9999999999999,
    lengthBuscaConta = 3,
    maxLength = 13,
    isUndefined = false,
    isNull = null,
    setInvalid = false,
    iniInicializado = false,
    isDisabled = false,
    label = 'Conta Contábil',
    focus = true,
  } = props;
  const timeoutRef = useRef<number | null>(null);

  const [inicializado, setInicializado] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [mensagemBusca, setMensagemBusca] = useState<string>('');
  const [valueInput, setValueInput] = useState<any>(null);

  const [conta, setConta] = useState<Conta>({
    cod_conta: 0,
    conta_contabil: '',
    conta_reduzida: '',
    des_conta_contabil: '',
  });

  // IDs estáveis para A11y
  const inputId = useMemo(
    () => `conta-contabil-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );

  const labelId = `${inputId}-label`;
  const descId = `${inputId}-desc`;

  useEffect(() => {
    if (value === '') {
      setConta({
        cod_conta: 0,
        conta_contabil: '',
        conta_reduzida: '',
        des_conta_contabil: '',
      });
      setMensagemBusca('');
      setValueInput('');
    }
    if (value !== '' && (valueInput === '' || valueInput === null)) {
      setValueInput(value);

      handleContas(String(value));
    }
  }, [value]);

  const validation = useCallback(() => {
    if (inicializado && isRequired) {
      if (isUndefined && valueInput === undefined) {
        setIsInvalid(true);
        return;
      }
      if (isNull && valueInput === null) {
        setIsInvalid(true);
        return;
      }
    }
    if (inicializado) {
      if (Number(valueInput) < min) {
        setIsInvalid(true);
        return;
      }
      if (Number(valueInput) > max) {
        setIsInvalid(true);
        return;
      }
    }

    setIsInvalid(false);
    setInicializado(true);
  }, [valueInput, inicializado, isNull, isRequired, isUndefined, max, min]);

  useEffect(() => {
    if (inicializado && isRequired) {
      setIsInvalid(setInvalid);
    }
  }, [inicializado, isRequired, setInvalid]);
  useEffect(() => {
    setInicializado(iniInicializado);
  }, [iniInicializado]);

  const handleContas = useCallback(
    async (value_cod_conta: string) => {
      if (value_cod_conta) {
        setMensagemBusca('');
        const valueParseString = String(value_cod_conta);

        if (valueParseString.length > lengthBuscaConta) {
          try {
            const { data } = await api.get(
              `/contaContabil/${valueParseString}`,
            );
            if (data.success) {
              setConta({
                cod_conta: data.data[0].cod_conta,
                conta_contabil: data.data[0].conta_contabil,
                conta_reduzida: data.data[0].conta_reduzida,
                des_conta_contabil: data.data[0].des_conta_contabil,
              });

              onChange(value_cod_conta, false, data.data[0].cod_conta);
            }
          } catch (error: any) {
            setConta({
              cod_conta: 0,
              conta_contabil: '',
              conta_reduzida: '',
              des_conta_contabil: '',
            });
            setMensagemBusca('Conta contábil não encontrada');
            onChange(value_cod_conta, true, 0);
          }
        }
      } else {
        onChange(value_cod_conta, true, 0);
      }
    },
    [lengthBuscaConta, onChange, value],
  );

  // Essa função evita que seja feita uma requisição
  // a cada caractere digitado. Em vez disso, espera 500ms após o último
  // caractere antes de realizar a requisição.
  const debouncedHandleContas = (cod_conta_input: any) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      handleContas(cod_conta_input);
    }, 500);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key.toLowerCase() === 'e' || event.key.toLowerCase() === '-') {
      event.preventDefault();
    }
  };

  return (
    <>
      <ModalContabil
        showModal={showModal}
        value={value}
        setConta={setConta}
        onChange={(val: any, isInvalid2: any, cod_conta: number) => {
          setValueInput(String(val ?? ''));
          setIsInvalid(false);
          onChange(val, true, cod_conta);
        }}
        setShowModal={setShowModal}
      />

      <AlertBox>
        <div>
          <label
            id={labelId}
            htmlFor={inputId}
            style={{ marginBottom: '-15px', marginTop: '10px' }}
            className="label-conta-contabil"
          >
            {label}
          </label>

          <div className="grupo">
            <input
              id={inputId}
              aria-labelledby={labelId}
              aria-describedby={descId}
              aria-invalid={(inicializado && isInvalid) || undefined}
              className={`${inicializado && isInvalid ? 'alert-class' : ''}`}
              type="number"
              min={min}
              max={max}
              step="1"
              inputMode="numeric"
              disabled={isDisabled}
              value={valueInput}
              placeholder={placeholder}
              onKeyDown={handleKeyDown}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const val = e.target.value;
                if (val.length <= maxLength) {
                  setConta({
                    cod_conta: 0,
                    conta_contabil: '',
                    conta_reduzida: '',
                    des_conta_contabil: '',
                  });

                  debouncedHandleContas(val);
                  setValueInput(val);
                  validation();
                }
              }}
              style={
                valueInput ? { textAlign: 'right' } : { textAlign: 'left' }
              }
            />

            <CustomButtonNovo
              style={{ backgroundColor: '#2773ca' }}
              onClick={() => setShowModal(true)}
              aria-label="Abrir busca de Conta Contábil"
              title="Abrir busca de Conta Contábil"
              icon={FaRegWindowRestore}
              width="7.5rem"
              iconOnly
            />

            <Span
              className="span"
              value={
                conta.conta_contabil !== ''
                  ? `${conta.conta_contabil} | ${conta.des_conta_contabil}`
                  : mensagemBusca
              }
            />
          </div>
        </div>
      </AlertBox>
    </>
  );
};

export default ContaContabil;
